import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    Modal,
    TextField,
    ListItemText,
    Collapse,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Select,
    MenuItem,
    IconButton,
    Checkbox,
    Paper,
    Chip,
    TableContainer,
    Divider,
    Alert,
} from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import DashboardLayout from './DashboardLayout';
import axios from 'axios';
import moment from 'moment';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import '../App.css';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { Hourglass } from 'react-loader-spinner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const EscritorioPage = () => {
    const [openEmpregados, setOpenEmpregados] = useState(false);
    const [empregados, setEmpregados] = useState([]);
    const [selectedEmpregado, setSelectedEmpregado] = useState(null);
    const [ferias, setFerias] = useState([]);
    const [faltas, setFaltas] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');
    const [showCalendar, setShowCalendar] = useState(false);
    const [selectedFerias, setSelectedFerias] = useState([]);
    const [selectedFerias2, setSelectedFerias2] = useState([]);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedYear, setSelectedYear] = useState(moment().year());
    const [yearsAvailable, setYearsAvailable] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionComplete, setSubmissionComplete] = useState(false);
    const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());

    const apiBaseURL = 'https://plasticemployers.duckdns.org';      // IP da rede local

    useEffect(() => {
        const years = Array.from(
            new Set(ferias.flatMap((feria) => parseDiasFerias(feria.dias_ferias).map((dia) => moment(dia).year())))
        ).sort((a, b) => b - a);
        setYearsAvailable(years);
    }, [ferias]);

    useEffect(() => {
        if (yearsAvailable.length > 0 && !yearsAvailable.includes(selectedYear)) {
            setSelectedYear(yearsAvailable[0]); // Defina o primeiro ano disponível como padrão
        }
    }, [yearsAvailable]);

    function parseDiasFerias(diasFerias) {
        if (!diasFerias) return [];
        return diasFerias
            .split(',')
            .map((dia) => dia.trim())
            .filter(Boolean)
            .filter((dia) => moment(dia, 'YYYY-MM-DD', true).isValid());
    }

    const feriasEntries = React.useMemo(() => (
        ferias.flatMap((feria) =>
            parseDiasFerias(feria.dias_ferias).map((dia) => ({
                key: `${feria.id}-${dia}`,
                feriaId: feria.id,
                dia,
                data_marcada: feria.data_marcada,
                aprovado_por: feria.aprovado_por,
                data_aprovacao: feria.data_aprovacao,
                half_day: Boolean(feria.half_day),
                empregado: feria.empregado,
            }))
        )
    ), [ferias]);

    const filteredFeriasEntries = React.useMemo(() => (
        feriasEntries.filter((entry) => moment(entry.dia).year() === selectedYear)
    ), [feriasEntries, selectedYear]);

    const calendarEntriesByDate = React.useMemo(() => {
        const grouped = {};
        filteredFeriasEntries.forEach((entry) => {
            if (!grouped[entry.dia]) {
                grouped[entry.dia] = [];
            }
            grouped[entry.dia].push(entry);
        });

        Object.values(grouped).forEach((entries) => {
            entries.sort((a, b) => {
                if (Boolean(a.aprovado_por) !== Boolean(b.aprovado_por)) {
                    return a.aprovado_por ? 1 : -1; // pendentes primeiro
                }
                return new Date(a.dia) - new Date(b.dia);
            });
        });

        return grouped;
    }, [filteredFeriasEntries]);

    const selectedCalendarDateKey = moment(selectedCalendarDate).format('YYYY-MM-DD');
    const selectedCalendarEntries = calendarEntriesByDate[selectedCalendarDateKey] || [];

    const selectedCalendarStats = React.useMemo(() => {
        const stats = {
            total: selectedCalendarEntries.length,
            pending: 0,
            approved: 0,
            halfDays: 0,
            selected: 0,
        };

        selectedCalendarEntries.forEach((entry) => {
            if (entry.aprovado_por) stats.approved += 1;
            else stats.pending += 1;
            if (entry.half_day) stats.halfDays += 1;
            if (selectedFerias2.includes(entry.key)) stats.selected += 1;
        });

        return stats;
    }, [selectedCalendarEntries, selectedFerias2]);

    const feriasSummary = React.useMemo(() => {
        const pending = filteredFeriasEntries.filter((entry) => !entry.aprovado_por).length;
        const approved = filteredFeriasEntries.filter((entry) => entry.aprovado_por).length;
        const halfDays = filteredFeriasEntries.filter((entry) => entry.half_day).length;

        return {
            total: filteredFeriasEntries.length,
            pending,
            approved,
            halfDays,
            selectedTable: selectedFerias.length,
            selectedCalendar: selectedFerias2.length,
        };
    }, [filteredFeriasEntries, selectedFerias, selectedFerias2]);

    const handleCalendarDateSelection = (date) => {
        const formattedDate = moment(date).format('YYYY-MM-DD'); // Garante o formato completo da data
        setSelectedCalendarDate(date);

        const entries = calendarEntriesByDate[formattedDate] || [];
        const pendingEntries = entries.filter((entry) => !entry.aprovado_por);

        // Se houver exatamente uma pendente nesse dia, permite toggle rápido no clique.
        if (pendingEntries.length === 1) {
            const feriaKey = pendingEntries[0].key;
            setSelectedFerias2((prevSelected) =>
                prevSelected.includes(feriaKey)
                    ? prevSelected.filter((key) => key !== feriaKey)
                    : [...prevSelected, feriaKey]
            );
        }
    };

    const handleApproveSelectedFromCalendar = async () => {
        try {
            if (selectedFerias2.length === 0) {
                alert('Nenhuma data selecionada para aprovação.');
                return;
            }

            setIsSubmitting(true); // Inicia o carregamento
            setSubmissionComplete(false);
    
            const token = localStorage.getItem('access');
            const user = JSON.parse(localStorage.getItem('user'));
            const approvedDates = []; // Array para acumular as datas aprovadas
    
            for (const key of selectedFerias2) {
                // Dividir a chave em partes (ID, ano, mês, dia)
                const parts = key.split('-');
                if (parts.length !== 4) {
                    console.error(`Formato inválido para key: ${key}`);
                    alert(`Erro: Chave inválida recebida: ${key}`);
                    continue;
                }
    
                const [feriaId, year, month, day] = parts;
                const dia = `${year}-${month}-${day}`;
                console.log("Data construída:", dia);
    
                const matchingFeria = ferias.find((feria) => feria.id === parseInt(feriaId));
                if (!matchingFeria) {
                    console.error(`Nenhuma feria encontrada para o ID: ${feriaId}`);
                    continue;
                }
    
                const payload = {
                    aprovado_por: user.username,
                    data_aprovacao: moment().format('YYYY-MM-DD'),
                    dias_ferias: dia,
                    ano: matchingFeria.ano,
                    data_marcada: matchingFeria.data_marcada,
                    empregado: matchingFeria.empregado,
                };
    
                console.log("Enviando PUT para ID:", feriaId, "com payload:", payload);
    
                await axios.put(`${apiBaseURL}/api/ferias/${feriaId}/`, payload, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
    
                // Adicionar a data aprovada ao array
                approvedDates.push(dia);
            }
    
            // Enviar uma única notificação com todas as datas aprovadas
            if (approvedDates.length === 1) {
                await sendNotification(
                    `O seu dia de férias em ${moment(approvedDates[0]).format('DD/MM/YYYY')} foi aprovado.`,
                    selectedEmpregado.id
                );
            } else if (approvedDates.length > 1) {
                const formattedDates = approvedDates
                    .map((date) => moment(date).format('DD/MM/YYYY'))
                    .join(', ');
                await sendNotification(
                    `Os teus dias de férias em ${formattedDates} foram aprovados.`,
                    selectedEmpregado.id
                );
            }
    
            setSubmissionComplete(true); // Marca como concluído
            setTimeout(() => setIsSubmitting(false), 1500);
            handleEmpregadoClick(selectedEmpregado); // Atualizar as informações
        } catch (error) {
            console.error('Erro ao aprovar as datas:', error.response ? error.response.data : error.message);
            alert('Erro ao aprovar as datas. Tente novamente.');
            setIsSubmitting(false);
        }
    };
    
    

    const handleYearChange = (event) => {
        setSelectedYear(event.target.value);
        setSelectedFerias2([]);
    };

    // Filtro dinâmico
    const filteredEmpregados = empregados.filter(empregado =>
        (empregado.nome_completo || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
    const [newFalta, setNewFalta] = useState({
        dia: '',
        tipo_falta: '',
        horario_inicio: '',
        horario_fim: '',
        documento_justificacao: null,
    });

    useEffect(() => {

        const token = localStorage.getItem('access');
            if (!token) {
                console.error('Token JWT não encontrado.');
            } else {
                console.log('Token JWT encontrado:', token);
            }
        const user = JSON.parse(localStorage.getItem('user'));

        // Verifica se o utilizador faz parte das seccoes "Admin" ou "Escritorio"
        if (user.seccao !== 'Admin' && user.seccao !== 'Escritorio') {
            setError('Você não faz parte da secção "Admin" ou "Escritorio". Acesso negado.');
            return;
        }

        const fetchEmpregados = async () => {
            const token = localStorage.getItem('access');
            console.log('token', token);

            try {
                const response = await axios.get(`${apiBaseURL}/api/empregados/`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setEmpregados(response.data);
            } catch (error) {
                console.error('Erro ao buscar empregados:', error);
            }
        };
        fetchEmpregados();
    }, []);

    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewFalta({
            ...newFalta,
            [name]: value,
        });
    };

    const handleEmpregadoClick = async (empregado) => {
        setSelectedEmpregado(empregado);
        setActiveSection('');
        setShowCalendar(false);
        setFerias([]);
        setSelectedFerias([]);
        setSelectedFerias2([]);
        setSelectedCalendarDate(new Date());
        setOpenEmpregados(false); // Fechar o Collapse

        const token = localStorage.getItem('access');
        try {
            const responseFaltas = await axios.get(`${apiBaseURL}/api/faltas/?empregado=${empregado.id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            const responseFerias = await axios.get(`${apiBaseURL}/api/ferias/?empregado=${empregado.id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFaltas(responseFaltas.data);
            setFerias(responseFerias.data);
        } catch (error) {
            console.error('Erro ao buscar faltas ou férias:', error);
        }
    };

    const handleToggleEmpregados = () => setOpenEmpregados(!openEmpregados);

    const handleDeleteEmpregado = async () => {
        const token = localStorage.getItem('access');
    
        // Exibir um alerta de confirmação antes de excluir
        const confirmDelete = window.confirm(`Tem certeza que deseja apagar o empregado ${selectedEmpregado?.nome}?`);
        
        if (!confirmDelete) return; // Se o usuário cancelar, não faz nada
    
        try {
            await axios.delete(`${apiBaseURL}/api/empregados/${selectedEmpregado.id}/`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
    
            setSelectedEmpregado(null);
            setEmpregados(empregados.filter(e => e.id !== selectedEmpregado.id));
    
            alert('Empregado apagado com sucesso!');
        } catch (error) {
            console.error('Erro ao apagar empregado:', error);
            alert('Erro ao apagar empregado. Tente novamente.');
        }
    };
    
    const handleFaltaUpdate = async (faltaId) => {
        try {
            const token = localStorage.getItem('access');
            const faltaToUpdate = faltas.find(f => f.id === faltaId);

            const formData = new FormData();
            formData.append('tipo_falta', faltaToUpdate.tipo_falta);
            formData.append('horario_inicio', faltaToUpdate.horario_inicio);
            formData.append('horario_fim', faltaToUpdate.horario_fim);
            formData.append('dia', faltaToUpdate.dia);
            formData.append('empregado', selectedEmpregado.id);

            if (faltaToUpdate.documento_justificacao && typeof faltaToUpdate.documento_justificacao !== 'string') {
                formData.append('documento_justificacao', faltaToUpdate.documento_justificacao);
            }

            await axios.put(`${apiBaseURL}/api/faltas/${faltaId}/`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            alert('Falta atualizada com sucesso!');
            handleEmpregadoClick(selectedEmpregado);
        } catch (error) {
            console.error('Erro ao atualizar a falta:', error.response ? error.response.data : error.message);
            alert('Erro ao atualizar a falta. Por favor, tente novamente.');
        }
    };

    const handleFileChange = async (event, faltaId) => {
        const file = event.target.files[0];
        if (file) {
            const token = localStorage.getItem('access');
            const faltaToUpdate = faltas.find(f => f.id === faltaId);

            const formData = new FormData();
            formData.append('documento_justificacao', file);
            formData.append('tipo_falta', 'justificada');
            formData.append('horario_inicio', faltaToUpdate.horario_inicio);
            formData.append('horario_fim', faltaToUpdate.horario_fim);
            formData.append('dia', faltaToUpdate.dia);
            formData.append('empregado', selectedEmpregado.id);

            try {
                await axios.put(`${apiBaseURL}/api/faltas/${faltaId}/`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                });

                alert('Documento anexado com sucesso e falta Justificada!');
                handleEmpregadoClick(selectedEmpregado);
            } catch (error) {
                console.error('Erro ao anexar o documento:', error.response ? error.response.data : error.message);
                alert('Erro ao anexar o documento. Por favor, tente novamente.');
            }
        }
    };

    const handleRejectFeria = async (feriaId) => {
        try {
            const token = localStorage.getItem('access');
            const feria = ferias.find(f => f.id === parseInt(feriaId));
    
            if (!feria) {
                alert('Erro: Féria não encontrada.');
                return;
            }
    
            await axios.delete(`${apiBaseURL}/api/ferias/${feriaId}/`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
    
            alert(`Féria para o dia ${moment(feria.dias_ferias).format('DD/MM/YYYY')} rejeitada com sucesso!`);
            handleEmpregadoClick(selectedEmpregado); // Atualizar as informações
        } catch (error) {
            console.error('Erro ao rejeitar a férias:', error);
            alert('Erro ao rejeitar a férias.');
        }
    };
    

    const handleToggleFeria = (feria, dia) => {
        const formattedDia = moment(dia, 'YYYY-MM-DD').format('YYYY-MM-DD');
        const feriaKey = `${feria.id}-${formattedDia}`;
    
        setSelectedFerias((prevSelected) =>
            prevSelected.includes(feriaKey)
                ? prevSelected.filter((key) => key !== feriaKey) // Remove se já estiver selecionado
                : [...prevSelected, feriaKey] // Adiciona se não estiver selecionado
        );
    };

    const handleToggleCalendarFeriaByKey = (feriaKey) => {
        setSelectedFerias2((prevSelected) =>
            prevSelected.includes(feriaKey)
                ? prevSelected.filter((key) => key !== feriaKey)
                : [...prevSelected, feriaKey]
        );
    };

    const handleApproveSelected = async () => {
        try {
            if (selectedFerias.length === 0) {
                console.error('Nenhuma data selecionada para aprovação.');
                return;
            }
    
            setIsSubmitting(true); // Inicia o carregamento
            setSubmissionComplete(false);
    
            const token = localStorage.getItem('access');
            const user = JSON.parse(localStorage.getItem('user'));
            const approvedDates = []; // Acumulará as datas aprovadas
    
            for (const key of selectedFerias) {
                const parts = key.split('-');
                if (parts.length !== 4) {
                    console.error(`Formato inválido para key: ${key}`);
                    continue;
                }
    
                const [feriaId, year, month, day] = parts;
                const formattedDia = `${year}-${month}-${day}`; // Reconstrói a data
                const feria = ferias.find(f => f.id === parseInt(feriaId));
    
                if (feria) {
                    // Monta o payload para envio
                    const payload = {
                        ...feria,
                        aprovado_por: user.username,
                        data_aprovacao: moment().format('YYYY-MM-DD'),
                        dias_ferias: formattedDia,
                    };
    
                    // Faz o PUT para aprovar as férias
                    await axios.put(`${apiBaseURL}/api/ferias/${feria.id}/`, payload, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
    
                    // Adiciona a data aprovada ao array
                    approvedDates.push(moment(formattedDia).format('DD/MM/YYYY'));
                }
            }
    
            // Envia uma única notificação consolidada com todas as datas aprovadas
            if (approvedDates.length > 0) {
                if (approvedDates.length === 1) {
                    await sendNotification(
                        `O seu dia de férias em ${approvedDates[0]} foi aprovado.`,
                        selectedEmpregado.id
                    );
                } else {
                    const combinedDates = approvedDates.join(', ');
                    await sendNotification(
                        `Os teus dias de férias em ${combinedDates} foram aprovados.`,
                        selectedEmpregado.id
                    );
                }
            }
    
            // Marca como concluído e finaliza o carregamento
            setSubmissionComplete(true);
            setTimeout(() => setIsSubmitting(false), 1500);
    
            // Atualiza as informações do empregado
            handleEmpregadoClick(selectedEmpregado);
        } catch (error) {
            console.error('Erro ao aprovar as férias:', error);
            setIsSubmitting(false); // Finaliza o carregamento em caso de erro
        }
    };

    const handleApproveFeria = async (feria) => {
        try {
            setIsSubmitting(true); // Inicia o carregamento
            setSubmissionComplete(false);
    
            const token = localStorage.getItem('access');
            const user = JSON.parse(localStorage.getItem('user'));
            const approvedDates = []; // Acumulará as datas aprovadas
    
            // Monta o payload para envio
            const payload = {
                ...feria,
                aprovado_por: user.username,
                data_aprovacao: moment().format('YYYY-MM-DD'),
                dias_ferias: feria.dias_ferias,
            };
    
            // Faz o PUT para aprovar as férias
            await axios.put(`${apiBaseURL}/api/ferias/${feria.id}/`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
    
            // Adiciona a data aprovada ao array
            approvedDates.push(moment(feria.dias_ferias).format('DD/MM/YYYY'));
    
            // Verifica o número de dias aprovados para decidir o formato da notificação
            if (approvedDates.length === 1) {
                await sendNotification(
                    `O seu dia de férias em ${approvedDates[0]} foi aprovado.`,
                    selectedEmpregado.id
                );
            } else {
                const combinedDates = approvedDates.join(', ');
                await sendNotification(
                    `Os teus dias de férias em ${combinedDates} foram aprovados.`,
                    selectedEmpregado.id
                );
            }
    
            // Marca como concluído e finaliza o carregamento
            setSubmissionComplete(true);
            setTimeout(() => setIsSubmitting(false), 1500);
    
            // Atualiza as informações do empregado
            handleEmpregadoClick(selectedEmpregado);
        } catch (error) {
            console.error('Erro ao aprovar as férias:', error);
            setIsSubmitting(false); // Finaliza o carregamento em caso de erro
        }
    };

    const handleRejectSelected = async () => {
        try {
            if (selectedFerias.length === 0) {
                console.error('Nenhuma data selecionada para rejeição.');
                return;
            }
    
            setIsSubmitting(true); // Inicia o carregamento
            setSubmissionComplete(false);
    
            const token = localStorage.getItem('access');
            const rejectedDates = []; // Armazena as datas rejeitadas
    
            for (const key of selectedFerias) {
                const [feriaId] = key.split('-');
                const feria = ferias.find(f => f.id === parseInt(feriaId));
    
                if (feria) {
                    // Executa a rejeição da feria no backend
                    await axios.delete(`${apiBaseURL}/api/ferias/${feriaId}/`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
    
                    // Adiciona a data rejeitada ao array
                    rejectedDates.push(moment(feria.dias_ferias).format('DD/MM/YYYY'));
                }
            }
    
            // Envia uma única notificação consolidada com todas as datas rejeitadas
            if (rejectedDates.length > 0) {
                if (rejectedDates.length === 1) {
                    await sendNotification(
                        `O seu dia de férias em ${rejectedDates[0]} foi rejeitado.`,
                        selectedEmpregado.id
                    );
                } else {
                    const combinedDates = rejectedDates.join(', ');
                    await sendNotification(
                        `Os teus dias de férias em ${combinedDates} foram rejeitados.`,
                        selectedEmpregado.id
                    );
                }
            }
    
            // Marca como concluído e finaliza o carregamento
            setSubmissionComplete(true);
            setTimeout(() => setIsSubmitting(false), 1500);
    
            // Atualiza as informações do empregado
            handleEmpregadoClick(selectedEmpregado);
        } catch (error) {
            console.error('Erro ao rejeitar as férias:', error);
            setIsSubmitting(false); // Finaliza o carregamento em caso de erro
        }
    };

    const renderCalendarWithMarkedDates = () => {
        return (
            <Box
                sx={{
                    '& .react-calendar': {
                        width: '100%',
                        border: 'none',
                        borderRadius: 2,
                        backgroundColor: '#fafafa',
                        p: 1.5,
                    },
                    '& .react-calendar__navigation button': {
                        borderRadius: 1,
                        fontWeight: 700,
                    },
                    '& .react-calendar__tile': {
                        minHeight: 84,
                        borderRadius: 1.5,
                        p: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                    },
                    '& .react-calendar__tile:enabled:hover': {
                        backgroundColor: '#eef6ff',
                    },
                    '& .react-calendar__tile--active': {
                        backgroundColor: '#dbeafe',
                        color: '#111827',
                    },
                    '& .react-calendar__tile--now': {
                        backgroundColor: '#fff7cc',
                    },
                    '& .office-cal__tile--has': {
                        boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.18)',
                    },
                    '& .office-cal__tile--busy': {
                        background: 'linear-gradient(180deg, rgba(255,243,224,0.8) 0%, rgba(255,255,255,1) 65%)',
                        boxShadow: 'inset 0 0 0 2px rgba(237, 108, 2, 0.25)',
                    },
                    '& .office-cal__tile--dense': {
                        background: 'linear-gradient(180deg, rgba(255,235,238,0.8) 0%, rgba(255,255,255,1) 65%)',
                        boxShadow: 'inset 0 0 0 2px rgba(211, 47, 47, 0.25)',
                    },
                    '& .office-cal__tile--picked': {
                        boxShadow: 'inset 0 0 0 2px rgba(25, 118, 210, 0.35)',
                    },
                    '& .office-cal__tile--selected-day': {
                        boxShadow: 'inset 0 0 0 2px #1976d2',
                    },
                }}
            >
                <Calendar
                    value={selectedCalendarDate}
                    onClickDay={handleCalendarDateSelection}
                    tileClassName={({ date, view }) => {
                        if (view !== 'month') return null;

                        const formattedDate = moment(date).format('YYYY-MM-DD');
                        const entries = calendarEntriesByDate[formattedDate] || [];
                        const count = entries.length;
                        const hasPicked = entries.some((entry) => selectedFerias2.includes(entry.key));

                        const classes = [];
                        if (count > 0) classes.push('office-cal__tile--has');
                        if (count >= 3) classes.push('office-cal__tile--busy');
                        if (count >= 6) classes.push('office-cal__tile--dense');
                        if (hasPicked) classes.push('office-cal__tile--picked');
                        if (moment(date).isSame(selectedCalendarDate, 'day')) classes.push('office-cal__tile--selected-day');

                        return classes.join(' ');
                    }}
                    tileContent={({ date, view }) => {
                        if (view !== 'month') return null;

                        const formattedDate = moment(date).format('YYYY-MM-DD');
                        const entries = calendarEntriesByDate[formattedDate] || [];
                        if (entries.length === 0) return null;

                        const approvedCount = entries.filter((entry) => Boolean(entry.aprovado_por)).length;
                        const pendingCount = entries.length - approvedCount;

                        return (
                            <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                <Box
                                    sx={{
                                        minWidth: 22,
                                        px: 0.75,
                                        borderRadius: 999,
                                        bgcolor: entries.length >= 6 ? 'error.main' : entries.length >= 3 ? 'warning.main' : 'primary.main',
                                        color: '#fff',
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        lineHeight: 1.35,
                                        textAlign: 'center',
                                    }}
                                >
                                    {entries.length}
                                </Box>
                                <Typography sx={{ fontSize: '0.58rem', lineHeight: 1, color: 'text.secondary' }}>
                                    {approvedCount}A / {pendingCount}P
                                </Typography>
                            </Box>
                        );
                    }}
                />
            </Box>
        );
    };

    const sendNotification = async (message, recipientId) => {
        const token = localStorage.getItem('access');
        try {
            await axios.post(`${apiBaseURL}/api/notifications/`, {
                message,
                recipients: [recipientId],
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (error) {
            console.error('Erro ao enviar notificação:', error);
        }
    };

    const extractDatesFromList = () => {
        const firstDate = filteredFeriasEntries[0]?.dia;
        if (firstDate) {
            setSelectedCalendarDate(moment(firstDate, 'YYYY-MM-DD').toDate());
        }
        setShowCalendar(true);
    };

    const handleSaveFalta = async () => {
        try {
            const token = localStorage.getItem('access');
            const formData = new FormData();

            formData.append('empregado', selectedEmpregado.id);
            formData.append('dia', newFalta.dia);
            formData.append('tipo_falta', newFalta.documento_justificacao ? 'em_aprovacao' : 'injustificada');
            formData.append('horario_inicio', newFalta.horario_inicio);
            formData.append('horario_fim', newFalta.horario_fim);
            if (newFalta.documento_justificacao) {
                formData.append('documento_justificacao', newFalta.documento_justificacao);
            }

            await axios.post(`${apiBaseURL}/api/faltas/`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            const message = `Uma falta foi criada para o dia ${moment(newFalta.dia).format('DD/MM/YYYY')} das ${newFalta.horario_inicio} às ${newFalta.horario_fim}.`;
            
            await axios.post(`${apiBaseURL}/api/notifications/`, {
                message: message,
                recipients: [selectedEmpregado.id],
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            alert('Falta criada com sucesso!');
            handleCloseModal();
        } catch (error) {
            console.error('Erro ao criar a falta:', error.response ? error.response.data : error.message);
            alert('Erro ao criar a falta. Por favor, tente novamente.');
        }
    };

    if (error) {
        return (
            <DashboardLayout>
                <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="h4" gutterBottom sx={{ mb: 0.5 }}>Escritório</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Gestão de empregados, férias e faltas
                    </Typography>
                </Box>
                {JSON.parse(localStorage.getItem('user')).seccao === 'Admin' && (
                    <Button variant="contained" color="primary" onClick={() => navigate('/ferramentas-administrativas')}>
                        Ferramentas Administrativas
                    </Button>
                )}
            </Box>

            <Box sx={{ mt: 2 }}>
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6">Empregados</Typography>
                        <Chip size="small" label={`${filteredEmpregados.length} resultados`} />
                    </Box>
                    <List component="nav" sx={{ p: 0 }}>
                        <ListItem
                            button
                            onClick={handleToggleEmpregados}
                            sx={{
                                borderRadius: 1.5,
                                bgcolor: 'action.hover',
                                mb: 1,
                            }}
                        >
                            <ListItemText
                                primary="Lista de Empregados"
                                secondary={openEmpregados ? 'Clique para recolher' : 'Clique para expandir e procurar'}
                            />
                            {openEmpregados ? <ExpandLess /> : <ExpandMore />}
                        </ListItem>
                        <Collapse in={openEmpregados} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 1 }}>
                                <TextField
                                    label="Procurar Empregado"
                                    fullWidth
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    size="small"
                                    sx={{ mb: 1.5 }}
                                />
                                <Paper variant="outlined" sx={{ maxHeight: 320, overflowY: 'auto' }}>
                                    <List component="div" disablePadding>
                                        {filteredEmpregados.map(empregado => (
                                            <ListItem
                                                button
                                                key={empregado.id}
                                                onClick={() => handleEmpregadoClick(empregado)}
                                                sx={{
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                    bgcolor: selectedEmpregado?.id === empregado.id ? 'action.selected' : 'transparent',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}
                                            >
                                                <ListItemText
                                                    primary={empregado.nome_completo}
                                                    secondary={`${empregado.seccao || '-'} • ${empregado.turno || '-'}`}
                                                />
                                            </ListItem>
                                        ))}
                                        {filteredEmpregados.length === 0 && (
                                            <ListItem>
                                                <ListItemText primary="Nenhum empregado encontrado." />
                                            </ListItem>
                                        )}
                                    </List>
                                </Paper>
                            </Box>
                        </Collapse>
                    </List>
                </Paper>
                {selectedEmpregado && (
                    <Box sx={{ mt: 4 }}>
                        <Paper sx={{ p: 2, mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Opções para: {selectedEmpregado.nome_completo}
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                <Chip label={`Seção: ${selectedEmpregado.seccao || '-'}`} size="small" />
                                <Chip label={`Turno: ${selectedEmpregado.turno || '-'}`} size="small" />
                                <Chip label={`Férias: ${feriasSummary.total}`} size="small" color="primary" variant="outlined" />
                                <Chip label={`Faltas: ${faltas.length}`} size="small" color="secondary" variant="outlined" />
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                <Button variant={activeSection === 'ferias' ? "contained" : "outlined"} onClick={() => setActiveSection('ferias')}>
                                    Férias
                                </Button>
                                <Button variant={activeSection === 'faltas' ? "contained" : "outlined"} onClick={() => setActiveSection('faltas')}>
                                    Faltas
                                </Button>
                                <Button variant="contained" color="error" onClick={handleDeleteEmpregado}>
                                    Apagar Conta
                                </Button>
                            </Box>
                        </Paper>
                        {activeSection === 'ferias' && (
                            <Box sx={{ mt: 4 }}>
                                <Paper sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6">Férias</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Gestão e aprovação de dias de férias do colaborador
                                            </Typography>
                                        </Box>
                                        {yearsAvailable.length > 0 && (
                                            <Select
                                                value={selectedYear}
                                                onChange={handleYearChange}
                                                size="small"
                                                sx={{ minWidth: 120 }}
                                            >
                                                {yearsAvailable.map(year => (
                                                    <MenuItem key={year} value={year}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    </Box>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        <Chip size="small" label={`Total: ${feriasSummary.total}`} />
                                        <Chip size="small" color="warning" variant="outlined" label={`Pendentes: ${feriasSummary.pending}`} />
                                        <Chip size="small" color="success" variant="outlined" label={`Aprovados: ${feriasSummary.approved}`} />
                                        <Chip size="small" color="secondary" variant="outlined" label={`Meios dias: ${feriasSummary.halfDays}`} />
                                        <Chip size="small" color="primary" variant="outlined" label={`Selecionados tabela: ${feriasSummary.selectedTable}`} />
                                        <Chip size="small" color="info" variant="outlined" label={`Selecionados calendário: ${feriasSummary.selectedCalendar}`} />
                                    </Box>

                                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 520 }}>
                                        <Table stickyHeader size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ width: 56 }}></TableCell>
                                                    <TableCell>Dia da marcação</TableCell>
                                                    <TableCell>Dia de férias</TableCell>
                                                    <TableCell>Status</TableCell>
                                                    <TableCell>Data de Aprovação</TableCell>
                                                    <TableCell>Meio dia</TableCell>
                                                    <TableCell>Ações</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(
                                                    filteredFeriasEntries
                                                        .sort((a, b) => new Date(a.dia) - new Date(b.dia))
                                                        .reduce((acc, item) => {
                                                            const month = moment(item.dia).format('MMMM');
                                                            if (!acc[month]) acc[month] = [];
                                                            acc[month].push(item);
                                                            return acc;
                                                        }, {})
                                                ).map(([month, items]) => (
                                                    <React.Fragment key={month}>
                                                        <TableRow>
                                                            <TableCell colSpan={7} sx={{ bgcolor: 'action.hover' }}>
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                                                                    {month}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                        {items.map(item => {
                                                            const feriaKey = `${item.feriaId}-${item.dia}`;
                                                            const isApproved = Boolean(item.aprovado_por);
                                                            return (
                                                                <TableRow key={feriaKey} hover selected={selectedFerias.includes(feriaKey)}>
                                                                    <TableCell padding="checkbox">
                                                                        <Checkbox
                                                                            checked={selectedFerias.includes(feriaKey)}
                                                                            disabled={isApproved}
                                                                            onChange={() => handleToggleFeria({ id: item.feriaId }, item.dia)}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>{moment(item.data_marcada).format('DD/MM/YYYY')}</TableCell>
                                                                    <TableCell>{moment(item.dia).format('DD/MM/YYYY')}</TableCell>
                                                                    <TableCell>
                                                                        <Chip
                                                                            size="small"
                                                                            label={isApproved ? 'Aprovado' : 'Em Aprovação'}
                                                                            color={isApproved ? 'success' : 'warning'}
                                                                            variant={isApproved ? 'filled' : 'outlined'}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell>{item.data_aprovacao ? moment(item.data_aprovacao).format('DD/MM/YYYY') : 'N/A'}</TableCell>
                                                                    <TableCell>
                                                                        {item.half_day ? <Chip size="small" label="1/2 dia" color="secondary" variant="outlined" /> : '-'}
                                                                    </TableCell>
                                                                    <TableCell>
                                                                        <IconButton
                                                                            onClick={() => handleApproveFeria({ ...item, id: item.feriaId, dias_ferias: item.dia })}
                                                                            color="success"
                                                                            disabled={isApproved}
                                                                        >
                                                                            <CheckIcon />
                                                                        </IconButton>
                                                                        <IconButton onClick={() => handleRejectFeria(item.feriaId)} color="error">
                                                                            <CloseIcon />
                                                                        </IconButton>
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </React.Fragment>
                                                ))}
                                                {filteredFeriasEntries.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={7}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Sem férias para o ano {selectedYear}.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 1.5, mt: 2 }}>
                                        <Button variant="contained" color="secondary" onClick={extractDatesFromList}>
                                            Ver em Calendário
                                        </Button>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                onClick={handleApproveSelected}
                                                disabled={selectedFerias.length === 0}
                                            >
                                                Confirmar selecionados
                                            </Button>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                onClick={handleRejectSelected}
                                                disabled={selectedFerias.length === 0}
                                            >
                                                Rejeitar selecionados
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            </Box>
                        )}
                        {activeSection === 'faltas' && (
                            <Box sx={{ mt: 4 }}>
                                <Paper sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6">Faltas</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Registo e atualização de faltas do colaborador
                                            </Typography>
                                        </Box>
                                        <Button variant="contained" color="primary" onClick={handleOpenModal}>
                                            Criar Nova Falta
                                        </Button>
                                    </Box>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Dia</TableCell>
                                                    <TableCell>Tipo de Falta</TableCell>
                                                    <TableCell>Horário de Início</TableCell>
                                                    <TableCell>Horário de Fim</TableCell>
                                                    <TableCell>Documento de Justificação</TableCell>
                                                    <TableCell>Ações</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {faltas.map((falta) => (
                                                    <TableRow key={falta.id} hover>
                                                        <TableCell>{moment(falta.dia).format('DD/MM/YYYY')}</TableCell>
                                                        <TableCell>
                                                            <Select
                                                                size="small"
                                                                value={falta.tipo_falta}
                                                                onChange={(e) => setFaltas(faltas.map(f => f.id === falta.id ? { ...f, tipo_falta: e.target.value } : f))}
                                                            >
                                                                <MenuItem value="justificada">Justificada</MenuItem>
                                                                <MenuItem value="injustificada">Injustificada</MenuItem>
                                                                <MenuItem value="em aprovacao">Em aprovação</MenuItem>
                                                            </Select>
                                                        </TableCell>
                                                        <TableCell>
                                                            <input type="time" value={falta.horario_inicio} onChange={(e) => setFaltas(faltas.map(f => f.id === falta.id ? { ...f, horario_inicio: e.target.value } : f))} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <input type="time" value={falta.horario_fim} onChange={(e) => setFaltas(faltas.map(f => f.id === falta.id ? { ...f, horario_fim: e.target.value } : f))} />
                                                        </TableCell>
                                                        <TableCell>
                                                            {falta.documento_justificacao ? (
                                                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                                                    <a href={falta.documento_justificacao} target="_blank" rel="noopener noreferrer">Ver Documento</a>
                                                                    <Button
                                                                        size="small"
                                                                        variant="text"
                                                                        component="label"
                                                                    >
                                                                        <EditIcon style={{ cursor: 'pointer', color: '#1976d2' }} />
                                                                        <input
                                                                            type="file"
                                                                            hidden
                                                                            onChange={(e) => handleFileChange(e, falta.id)}
                                                                        />
                                                                    </Button>
                                                                </Box>
                                                            ) : (
                                                                <Button
                                                                    variant="contained"
                                                                    component="label"
                                                                    color="primary"
                                                                    size="small"
                                                                >
                                                                    Anexar Documento
                                                                    <input
                                                                        type="file"
                                                                        hidden
                                                                        onChange={(e) => handleFileChange(e, falta.id)}
                                                                    />
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button size="small" variant="contained" onClick={() => handleFaltaUpdate(falta.id)}>Atualizar</Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {faltas.length === 0 && (
                                                    <TableRow>
                                                        <TableCell colSpan={6}>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Sem faltas registadas.
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>
                            </Box>
                        )}
                        {showCalendar && (
                            <Box
                                sx={{
                                    mt: 4,
                                    display: 'flex',
                                    flexDirection: { xs: 'column', lg: 'row' },
                                    alignItems: 'stretch',
                                    gap: 2,
                                }}
                            >
                                <Paper sx={{ flex: 1, p: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 1 }}>Calendário de Férias</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        <Chip size="small" label="Nº de pedidos no dia" color="primary" variant="outlined" />
                                        <Chip size="small" label="A = aprovado / P = pendente" color="warning" variant="outlined" />
                                        <Chip size="small" label="Clique no dia para ver detalhes" color="info" variant="outlined" />
                                    </Box>
                                    {renderCalendarWithMarkedDates()}
                                </Paper>

                                <Paper sx={{ width: { xs: '100%', lg: 360 }, p: 2, flexShrink: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                        {moment(selectedCalendarDate).format('DD/MM/YYYY')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        Pedidos de férias deste dia
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        <Chip size="small" label={`Total: ${selectedCalendarStats.total}`} />
                                        <Chip size="small" color="warning" variant="outlined" label={`Pendentes: ${selectedCalendarStats.pending}`} />
                                        <Chip size="small" color="success" variant="outlined" label={`Aprovados: ${selectedCalendarStats.approved}`} />
                                        <Chip size="small" color="secondary" variant="outlined" label={`Meios dias: ${selectedCalendarStats.halfDays}`} />
                                        <Chip size="small" color="primary" variant="outlined" label={`Selecionados: ${selectedCalendarStats.selected}`} />
                                    </Box>

                                    {selectedCalendarEntries.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Sem pedidos de férias neste dia.
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'grid', gap: 1, maxHeight: 360, overflowY: 'auto', pr: 0.5 }}>
                                            {selectedCalendarEntries.map((entry) => {
                                                const isApproved = Boolean(entry.aprovado_por);
                                                const isChecked = selectedFerias2.includes(entry.key);
                                                return (
                                                    <Box
                                                        key={entry.key}
                                                        sx={{
                                                            p: 1.25,
                                                            borderRadius: 1.5,
                                                            border: '1px solid',
                                                            borderColor: 'divider',
                                                            bgcolor: isChecked ? 'action.selected' : 'background.paper',
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={isChecked}
                                                                    disabled={isApproved}
                                                                    onChange={() => handleToggleCalendarFeriaByKey(entry.key)}
                                                                />
                                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                    {moment(entry.dia).format('DD/MM/YYYY')}
                                                                </Typography>
                                                            </Box>
                                                            <Chip
                                                                size="small"
                                                                label={isApproved ? 'Aprovado' : 'Pendente'}
                                                                color={isApproved ? 'success' : 'warning'}
                                                                variant={isApproved ? 'filled' : 'outlined'}
                                                            />
                                                        </Box>
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4.5 }}>
                                                            Pedido #{entry.feriaId}
                                                            {entry.half_day ? ' • Meio dia' : ''}
                                                            {entry.data_marcada ? ` • Marcado em ${moment(entry.data_marcada).format('DD/MM/YYYY')}` : ''}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    )}

                                    <Divider sx={{ my: 2 }} />
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        color="success"
                                        onClick={handleApproveSelectedFromCalendar}
                                        disabled={selectedFerias2.length === 0}
                                    >
                                        Aprovar Datas Selecionadas
                                    </Button>
                                </Paper>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
            <Modal open={modalOpen} onClose={handleCloseModal}>
                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, bgcolor: 'background.paper', boxShadow: 24, p: 4 }}>
                    <Typography id="modal-title" variant="h6" component="h2">
                        Adicionar Falta
                    </Typography>
                    <TextField
                        label="Empregado"
                        fullWidth
                        margin="normal"
                        value={selectedEmpregado ? selectedEmpregado.nome_completo : ''}
                        disabled
                    />
                    <TextField
                        label="Dia"
                        type="date"
                        name="dia"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={newFalta.dia}
                        onChange={handleChange}
                    />
                    <TextField
                        label="Horário de Início"
                        type="time"
                        name="horario_inicio"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={newFalta.horario_inicio}
                        onChange={handleChange}
                    />
                    <TextField
                        label="Horário de Fim"
                        type="time"
                        name="horario_fim"
                        fullWidth
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                        value={newFalta.horario_fim}
                        onChange={handleChange}
                    />
                    <Button
                        variant="contained"
                        component="label"
                        color="primary"
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        Anexar Documento de Justificação
                        <input
                            type="file"
                            hidden
                            onChange={(e) => setNewFalta({ ...newFalta, documento_justificacao: e.target.files[0] })}
                        />
                    </Button>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                        <Button onClick={handleCloseModal} variant="contained" color="secondary">
                            Cancelar
                        </Button>
                        <Button onClick={handleSaveFalta} variant="contained" color="primary">
                            Salvar Falta
                        </Button>
                    </Box>
                </Box>
            </Modal>
            <Modal open={isSubmitting} aria-labelledby="loading-modal" aria-describedby="loading-description">
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 300,
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {submissionComplete ? (
                        <>
                            <CheckCircleIcon color="success" sx={{ fontSize: 80 }} />
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Datas aprovadas com sucesso!
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Hourglass
                                visible={true}
                                height="80"
                                width="80"
                                ariaLabel="hourglass-loading"
                                colors={['#306cce', '#72a1ed']}
                            />
                            <Typography variant="h6" sx={{ mt: 2 }}>
                                Processando...
                            </Typography>
                        </>
                    )}
                </Box>
            </Modal>
        </DashboardLayout>
    );
};

export default EscritorioPage;
